import { useNavigate } from "@tanstack/react-router";
import { dataBR, faixa, fmt, limiaresDa, nomeEspecie, posologia, resultadoDose, toxCalc } from "@vetbolso/core";
import { Icone } from "@vetbolso/ui";
import { createContext, Fragment, type ReactNode, useContext, useEffect, useRef, useState } from "react";
import { FolhaBula } from "./bula.tsx";
import { Alertas, CampoNumero, CampoPeso, Grande, Refs, Rodape, refsUnicas, Secao, SpPill, textoPeso } from "./comp.tsx";
import { type Folha, useApp, useBase } from "./estado.tsx";

export const TOXCATS: [string, string][] = [
  ["todos", "Todos"],
  ["alimento", "Alimentos"],
  ["humano", "Remédio humano"],
  ["planta", "Plantas"],
  ["praguicida", "Praguicidas"],
  ["animal", "Animais"],
  ["outros", "Outros"],
];

/** Posição da folha na pilha: decide quem fica por cima. */
const NivelFolha = createContext(0);

export function Casca({
  titulo,
  meta,
  children,
  corpo,
}: {
  titulo: string;
  meta?: ReactNode;
  children: ReactNode;
  topo?: boolean;
  /** Elemento fixo entre o cabeçalho e o corpo rolável (navegação de seções). */
  corpo?: { antes?: ReactNode; ref?: React.Ref<HTMLDivElement> };
}) {
  const nivel = useContext(NivelFolha);
  const { fechar } = useApp();
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    ref.current?.focus();
    const esc = (e: KeyboardEvent) => e.key === "Escape" && fechar();
    window.addEventListener("keydown", esc);
    return () => window.removeEventListener("keydown", esc);
  }, [fechar]);
  return (
    <>
      <div className="scrim" style={{ zIndex: 5 + 2 * nivel }} onClick={fechar} aria-hidden="true" />
      <div className="sheet" style={{ zIndex: 6 + 2 * nivel }} role="dialog" aria-modal="true" aria-label={titulo} ref={ref} tabIndex={-1}>
        <div className="sheet-head">
          <div className="grip" />
          <div className="row1">
            <div style={{ display: "flex", flexDirection: "column", gap: 6, minWidth: 0 }}>
              <h3>{titulo}</h3>
              {meta ? <div style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center" }}>{meta}</div> : null}
            </div>
            <button type="button" className="x" onClick={fechar} aria-label="Fechar">
              <Icone nome="x" />
            </button>
          </div>
          {corpo?.antes}
        </div>
        <div className="sheet-body" ref={corpo?.ref}>
          {children}
        </div>
      </div>
    </>
  );
}

export function Folhas() {
  const { folhas } = useApp();
  return (
    <>
      {folhas.map((f, i) => (
        // biome-ignore lint/suspicious/noArrayIndexKey: pilha de no máximo três folhas
        <NivelFolha.Provider key={i} value={i}>
          <FolhaUma f={f} topo={i > 0} />
        </NivelFolha.Provider>
      ))}
    </>
  );
}

function FolhaUma({ f, topo }: { f: Folha; topo: boolean }) {
  if (f.t === "ref") return <FolhaFonte f={f} topo={topo} />;
  if (f.t === "bula") return <FolhaBula id={f.id} />;
  if (f.t === "dose") return <FolhaDose id={f.id} topo={topo} />;
  if (f.t === "tox") return <FolhaTox id={f.id} topo={topo} />;
  if (f.t === "prod") return <FolhaProduto id={f.id} topo={topo} />;
  if (f.t === "planta") return <FolhaPlanta id={f.id} topo={topo} />;
  return <FolhaRegra id={f.id} topo={topo} />;
}

function FolhaDose({ id, topo }: { id: string; topo: boolean }) {
  const base = useBase();
  const { peso, unPeso, setRx, fecharTodas, folhas, abrir } = useApp();
  const navegar = useNavigate();
  const d = base.doses.find((x) => x.id === id);
  if (!d) return null;
  const r = resultadoDose(d, peso);
  return (
    <Casca
      topo={topo}
      titulo={d.farmaco}
      meta={
        <>
          <SpPill id={d.especie} />
          <span className="sub">{d.classe}</span>
          {d.controle ? <span className="etq atencao">{d.controle}</span> : null}
        </>
      }
    >
      {d.semDose ? (
        <div className="omit">Sem dose para calcular. {d.contexto}</div>
      ) : (
        <Secao titulo={r?.tipo === "fixa" ? "Dose por animal" : "Cálculo pelo peso"} icone="calc" tom="dose">
          {r?.tipo === "fixa" ? null : <CampoPeso id="peso-sheet" />}
          {r?.tipo === "fixa" ? (
            <Grande v={`${faixa(r.lo, r.hi, 3)} ${r.un}`} l="dose total por animal, independe do peso" />
          ) : r?.tipo === "peso" ? (
            <>
              <Grande v={`${faixa(r.calc.lo, r.calc.hi, 3)} ${r.calc.base}`} l={`${r.quando} para ${textoPeso(peso, unPeso)}`} />
              {r.calc.vols.map((v) => (
                <Grande key={v.rotulo} cls="alt" v={`${faixa(v.lo, v.hi, 2)} mL${r.calc.suf}`} l={v.rotulo} />
              ))}
            </>
          ) : (
            <div className="omit">Sem base por peso: use o valor da ficha.</div>
          )}
        </Secao>
      )}
      <Secao titulo="Posologia" icone="syringe" tom="inter">
        <Dados
          linhas={[
            ["Dose", d.semDose ? null : `${faixa(d.min ?? 0, d.max, 3)} ${d.un}`],
            ["Via", d.via],
            ["Intervalo", d.intervalo],
            ["Duração", d.duracao],
            ["Contexto", d.semDose ? null : d.contexto],
          ]}
        />
      </Secao>
      {d.controle ? (
        <div className="alert warning">
          <Icone nome="alert" className="sm" />
          <div>
            <p>
              <b>{d.controle}.</b> {d.controleNota}
            </p>
            <Refs refs={d.controleRefs} compacto />
          </div>
        </div>
      ) : null}
      <Alertas lista={d.alertas} />
      {d.bula && base.bulario.some((b) => b.id === d.bula) && !folhas.some((f) => f.t === "bula") ? (
        <button type="button" className="btn ghost" onClick={() => abrir({ t: "bula", id: d.bula as string })}>
          <Icone nome="livroAberto" />
          Ver bulário: indicações, interações e efeitos
        </button>
      ) : null}
      <Rodape refs={d.refs} />
      {d.semDose ? null : (
        <button
          type="button"
          className="btn"
          onClick={() => {
            setRx((rx) => ({
              ...rx,
              ...(rx.peso ? {} : { peso: unPeso === "g" ? fmt(peso * 1000, 1) : fmt(peso, 2), pesoUn: unPeso }),
              itens: [...rx.itens, { farmaco: d.farmaco, pos: posologia(d, peso), controle: d.controle, refs: d.refs }],
            }));
            fecharTodas();
            navegar({ to: "/receita" });
          }}
        >
          <Icone nome="plus" />
          Adicionar à receita
        </button>
      )}
    </Casca>
  );
}

/** Linhas rótulo e valor, separadas por fio; omite as vazias. */
function Dados({ linhas }: { linhas: [string, ReactNode][] }) {
  const cheias = linhas.filter(([, v]) => v != null && v !== "" && v !== false);
  if (!cheias.length) return null;
  return (
    <dl className="dados">
      {cheias.map(([r, v]) => (
        <Fragment key={r}>
          <dt>{r}</dt>
          <dd>{v}</dd>
        </Fragment>
      ))}
    </dl>
  );
}

function FolhaTox({ id, topo }: { id: string; topo: boolean }) {
  const base = useBase();
  const { especie: e, peso } = useApp();
  const t = base.toxicos.find((x) => x.id === id);
  const [qtd, setQtd] = useState<number | null>(t?.calc?.exemplo ?? null);
  const [tipo, setTipo] = useState(0);
  if (!t) return null;
  const lim = limiaresDa(t, e);
  const c = toxCalc(t, e, peso, qtd, tipo);
  const antidotos = (t.antidotos ?? []).filter((a) => !a.especie || a.especie === e);
  return (
    <Casca
      topo={topo}
      titulo={t.agente}
      meta={
        <>
          <SpPill id={e} />
          <span className="sub">{TOXCATS.find((x) => x[0] === t.cat)?.[1]}</span>
        </>
      }
    >
      {t.resumo ? <p className="resumo">{t.resumo}</p> : null}
      {t.calc && lim.length ? (
        <Secao titulo="Quanto ingeriu?" icone="calc" tom="tox">
          <div className="fields linha">
            {t.calc.fatores ? (
              <div className="field">
                <label htmlFor="tx-tipo">Tipo</label>
                <select id="tx-tipo" value={tipo} onChange={(ev) => setTipo(Number(ev.target.value))}>
                  {t.calc.fatores.map((f, i) => (
                    <option key={f.rotulo} value={i}>
                      {f.rotulo}
                    </option>
                  ))}
                </select>
              </div>
            ) : null}
            <CampoNumero id="tx-qtd" rotulo={t.calc.entrada} valor={qtd} aoMudar={setQtd} />
            <CampoPeso id="tx-peso" rotulo="Peso" solto classe="curto" />
          </div>
          {c ? (
            <Grande
              cls={c.gravidade === "abaixo" ? "" : c.gravidade === "leve" ? "mid" : "bad"}
              v={`${fmt(c.valor, 2)} ${c.un}`}
              l={c.atingido ? c.atingido.texto : "Abaixo do menor limiar conferido. Não exclui risco: observar e ligar para o veterinário."}
            />
          ) : (
            <p className="dica">Informe quantidade e peso.</p>
          )}
          <Refs refs={t.calc.refs} compacto />
        </Secao>
      ) : null}
      {lim.length ? (
        <Secao titulo={`Limiares para ${nomeEspecie(e).toLowerCase()}`} icone="alert" tom="aviso" n={lim.length}>
          <ul className="itens">
            {lim.map((l) => (
              <li key={l.texto}>
                <div className="linha-top">
                  <p>{l.texto}</p>
                  {l.valor != null ? <span className="linha-v">≥ {`${fmt(l.valor, 2)} ${l.un ?? ""}`}</span> : null}
                </div>
              </li>
            ))}
          </ul>
          <Refs refs={refsUnicas(lim.map((l) => l.refs))} compacto />
        </Secao>
      ) : (
        <div className="omit">
          Sem limiar de dose conferido para {nomeEspecie(e).toLowerCase()}.{" "}
          {t.semLimiar || "Qualquer ingestão deve ser tratada como potencialmente grave."}
        </div>
      )}
      {t.sinais ? (
        <Secao titulo="Sinais" icone="efeito" tom="efeitos">
          <div className="texto">
            <p>{t.sinais}</p>
          </div>
        </Secao>
      ) : null}
      {t.conduta ? (
        <Secao titulo="Conduta inicial" icone="pulse" tom="ind">
          <div className="texto">
            <p>{t.conduta}</p>
          </div>
          {antidotos.length ? (
            <ul className="itens">
              {antidotos.map((a) => (
                <li key={a.nome}>
                  <b className="linha-nome">{a.nome}</b>
                  <p>{a.dose}</p>
                  <Refs refs={a.refs} compacto />
                </li>
              ))}
            </ul>
          ) : null}
        </Secao>
      ) : null}
      <Alertas lista={t.alertas} />
      <Rodape refs={t.refs} />
    </Casca>
  );
}

function FolhaProduto({ id, topo }: { id: string; topo: boolean }) {
  const p = useBase().produtos.find((x) => x.id === id);
  if (!p) return null;
  return (
    <Casca
      topo={topo}
      titulo={p.produto}
      meta={
        <>
          {p.especies.map((e) => (
            <SpPill key={e} id={e} />
          ))}
          <span className="sub">{p.fabricante}</span>
        </>
      }
    >
      <Secao titulo="Carência" icone="clock" tom="campo">
        <div className="grid2">
          <Grande cls="bad" v={p.carenciaCarne ?? "—"} l="para carne" />
          <Grande cls="mid" v={p.carenciaLeite ?? "—"} l="para leite" />
        </div>
        {p.leiteTexto ? <p className="dica">{p.leiteTexto}</p> : null}
      </Secao>
      <Secao titulo="Produto" icone="caixa" tom="inter">
        <Dados
          linhas={[
            ["Princípio ativo", [p.farmaco, p.concentracao].filter(Boolean).join(" · ")],
            ["Registro MAPA", p.registro],
            ["Dose", p.dose],
            ["Via", p.via],
          ]}
        />
      </Secao>
      <Alertas lista={p.alertas} />
      <div className="alert info">
        <Icone nome="info" className="sm" />
        <p>Carência vale para este produto. Outro produto com o mesmo princípio ativo pode ter outra carência.</p>
      </div>
      <Rodape refs={p.refs} />
    </Casca>
  );
}

/** Planta tóxica: efeito, dose letal e alertas, cada um com a fonte. */
function FolhaPlanta({ id, topo }: { id: string; topo: boolean }) {
  const p = useBase().plantas.find((x) => x.id === id);
  if (!p) return null;
  return (
    <Casca
      topo={topo}
      titulo={p.nome}
      meta={
        <>
          {p.cientifico ? <i className="sub">{p.cientifico}</i> : null}
          {p.especies.map((e) => (
            <SpPill key={e} id={e} />
          ))}
        </>
      }
    >
      {p.efeito ? <p className="resumo">{p.efeito}</p> : null}
      {p.dl ? (
        <Secao titulo="Dose letal" icone="alert" tom="tox">
          <Grande cls="bad" v={p.dl} l="conferida em experimento ou relato publicado" />
          <Refs refs={p.dlRefs} compacto />
        </Secao>
      ) : null}
      <Alertas lista={p.alertas} />
      {p.nota ? <p className="dica">{p.nota}</p> : null}
      <Rodape refs={p.refs} />
    </Casca>
  );
}

export function ItensRegra({ itens }: { itens?: { t: string; refs?: import("@vetbolso/contracts").Ref[] }[] }) {
  if (!itens?.length) return null;
  return (
    <ul className="itens">
      {itens.map((it) => (
        <li key={it.t}>
          <p>{it.t}</p>
          <Refs refs={it.refs} compacto />
        </li>
      ))}
    </ul>
  );
}

function FolhaRegra({ id, topo }: { id: string; topo: boolean }) {
  const r = useBase().regras.find((x) => x.id === id);
  if (!r) return null;
  return (
    <Casca topo={topo} titulo={r.titulo}>
      {r.resumo ? <p className="resumo">{r.resumo}</p> : null}
      {r.itens?.length ? (
        <Secao titulo="O que a regra pede" icone="check" tom="ind" n={r.itens.length}>
          <ItensRegra itens={r.itens} />
          {r.nota ? <p className="dica">{r.nota}</p> : null}
        </Secao>
      ) : r.nota ? (
        <p className="dica">{r.nota}</p>
      ) : null}
      <Rodape refs={r.refs} />
    </Casca>
  );
}

function FolhaFonte({ f: s, topo }: { f: Extract<Folha, { t: "ref" }>; topo: boolean }) {
  const base = useBase();
  const f = base.fontes.find((x) => x.id === s.f);
  if (!f) return null;
  const ok = f.status === "verificado" && !s.nv;
  return (
    <Casca
      topo={topo}
      titulo={f.sigla}
      meta={
        <>
          <span className={`etq ${ok ? "bom" : "atencao"}`}>{ok ? "conferida na fonte" : "valor pendente de confirmação"}</span>
          <span className="fonte-tipo">{f.tipo}</span>
        </>
      }
    >
      <p className="fonte-titulo">{f.titulo}</p>
      {s.cit ? <blockquote className="cita">“{s.cit}”</blockquote> : null}
      <Secao titulo="Documento" icone="file" tom={ok ? "ind" : "aviso"}>
        <Dados
          linhas={[
            ["Autoria", f.orgao],
            ["Ano", f.ano],
            ["Onde", s.loc],
            ["DOI", f.doi],
            ["Conferida", dataBR(f.acesso || base.meta?.conferidoEm)],
          ]}
        />
      </Secao>
      {f.nota ? (
        <div className="alert info">
          <Icone nome="info" className="sm" />
          <p>{f.nota}</p>
        </div>
      ) : null}
      {f.url ? (
        <>
          <a className="btn" href={f.url} target="_blank" rel="noopener noreferrer">
            <Icone nome="ext" />
            Abrir documento
          </a>
          <p className="dica quebra">{f.url}</p>
        </>
      ) : null}
    </Casca>
  );
}
